<script setup>
/**
 * 批量替换：把所有场景链里的某个 (厂商, 模型) 一次换成另一个，比如供应商出问题时整体切走。
 *
 * 逐个场景调用同一个保存接口，每个场景各自过后端配置校验；被拒的场景单独列出原因，其余照常生效。
 * 「把哪个换掉」通过 v-model:from 交给父组件，总表据此高亮受影响的场景。
 */
import { computed, ref } from 'vue'
import { ArrowRight, Replace } from '@lucide/vue'
import { Button, Modal, Select, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveLlmModeChain } from '../../../api/llm/index.js'
import { modelsInUse, replaceInChain, stepKey } from '../mode-chain.js'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').ModeRow[]>} */
  modes: { type: Array, required: true },
  /** @type {import('vue').PropType<import('../mode-chain.js').ChainStep[]>} */
  options: { type: Array, required: true },
  /** 选中的「要换掉的」stepKey；空串 = 没选。 */
  from: { type: String, default: '' },
})

const emit = defineEmits(['update:from', 'saved'])

const { t, te } = useI18n()
const toast = useToast()

const to = ref('')
const confirming = ref(false)
const running = ref(false)
/** @type {import('vue').Ref<Array<{ mode: string, message: string }>>} */
const failures = ref([])

const fromOptions = computed(() => modelsInUse(props.modes).map(step => ({
  value: stepKey(step),
  label: t('visualGameAdmin.llm.modes.bulk.usedBy', { model: `${step.provider} / ${step.model}`, count: step.count }),
})))
const toOptions = computed(() => props.options
  .filter(step => stepKey(step) !== props.from)
  .map(step => ({ value: stepKey(step), label: `${step.provider} / ${step.model}` })))
const affected = computed(() => (props.from ? props.modes.filter(mode => mode.chain.some(step => stepKey(step) === props.from)) : []))
const ready = computed(() => props.from !== '' && to.value !== '' && to.value !== props.from && affected.value.length > 0)

function modeName(mode) {
  const key = `visualGameAdmin.storyModes.${mode}.name`
  return te(key) ? t(key) : mode
}

function pickFrom(value) {
  emit('update:from', value)
  failures.value = []
  if (value === to.value) to.value = ''
}

async function apply() {
  running.value = true
  failures.value = []
  const targets = affected.value
  for (const mode of targets) {
    try {
      const saved = await saveLlmModeChain(mode.mode, { chain: replaceInChain(mode.chain, props.from, to.value), enabled: mode.enabled })
      emit('saved', saved)
    } catch (err) {
      failures.value.push({ mode: mode.mode, message: err.message })
    }
  }
  running.value = false
  confirming.value = false
  const done = targets.length - failures.value.length
  if (failures.value.length) toast.error(t('visualGameAdmin.llm.modes.bulk.partial', { done, failed: failures.value.length }), { duration: 0 })
  else {
    toast.success(t('visualGameAdmin.llm.modes.bulk.done', { count: done }))
    emit('update:from', '')
    to.value = ''
  }
}
</script>

<template>
  <div
    class="bulk-replace"
    data-insp-path="apps/visual-game-admin/src/pages/llm/mode/BulkReplace.vue#bulk"
  >
    <div class="bulk">
      <span class="bulk__label">{{ t('visualGameAdmin.llm.modes.bulk.title') }}</span>
      <div class="bulk__pick">
        <Select
          :model-value="from"
          :options="fromOptions"
          block
          :placeholder="t('visualGameAdmin.llm.modes.bulk.from')"
          :aria-label="t('visualGameAdmin.llm.modes.bulk.from')"
          @update:model-value="pickFrom"
        />
      </div>
      <ArrowRight
        class="bulk__arrow"
        :size="18"
        aria-hidden="true"
      />
      <div class="bulk__pick">
        <Select
          v-model="to"
          :options="toOptions"
          :disabled="!from"
          block
          :placeholder="t('visualGameAdmin.llm.modes.bulk.to')"
          :aria-label="t('visualGameAdmin.llm.modes.bulk.to')"
        />
      </div>
      <Button
        variant="primary"
        size="sm"
        :disabled="!ready"
        @click="confirming = true"
      >
        <Replace :size="16" />
        {{ affected.length ? t('visualGameAdmin.llm.modes.bulk.apply', { count: affected.length }) : t('visualGameAdmin.llm.modes.bulk.applyIdle') }}
      </Button>
    </div>

    <ul
      v-if="failures.length"
      class="bulk__failures"
      role="alert"
    >
      <li
        v-for="failure in failures"
        :key="failure.mode"
      >
        <strong>{{ modeName(failure.mode) }}</strong>{{ failure.message }}
      </li>
    </ul>

    <Modal
      v-model:open="confirming"
      :title="t('visualGameAdmin.llm.modes.bulk.confirmTitle', { count: affected.length })"
      :close-on-backdrop="!running"
      width="520px"
    >
      <p class="bulk__summary">
        <code>{{ from.replace('#', ' / ') }}</code>
        <ArrowRight
          :size="14"
          aria-hidden="true"
        />
        <code>{{ to.replace('#', ' / ') }}</code>
      </p>
      <ul class="bulk__targets">
        <li
          v-for="mode in affected"
          :key="mode.mode"
        >
          {{ modeName(mode.mode) }}
        </li>
      </ul>
      <template #footer>
        <Button
          variant="ghost"
          :disabled="running"
          @click="confirming = false"
        >
          {{ t('visualGameAdmin.llm.modes.bulk.cancel') }}
        </Button>
        <Button
          variant="primary"
          :loading="running"
          @click="apply"
        >
          {{ t('visualGameAdmin.llm.modes.bulk.confirm') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.bulk {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm) var(--drama-space-md);
}

.bulk__label {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
}

.bulk__pick {
  width: min(340px, 100%);
}

.bulk__arrow {
  flex: none;
  color: var(--drama-color-text-muted);
}

.bulk__failures {
  display: grid;
  gap: var(--drama-space-xs);
  margin: var(--drama-space-lg) 0 0;
  padding: var(--drama-space-md) var(--drama-space-md) var(--drama-space-md) calc(var(--drama-space-md) * 2);
  border-radius: var(--drama-radius-md);
  background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
  color: var(--drama-color-danger);
  font-size: var(--drama-font-size-sm);
}

.bulk__failures strong {
  margin-right: var(--drama-space-sm);
}

.bulk__summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin: 0 0 var(--drama-space-md);
}

.bulk__targets {
  display: grid;
  gap: var(--drama-space-xs);
  margin: 0;
  padding-left: calc(var(--drama-space-md) * 1.5);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
</style>
